import { DeductionListJson, TransactionJson, TransactionKind } from "@/api"
import { ACCOUNTING_COLUMNS, OPEN_CLEARED_STATES, is_open_cleared_filter, set_cleared_column_filter } from "./columns.tsx"
import { FieldType, OrgFieldConfig } from "@/api/org_field_config.tsx"
import { flexRender, Row, Table } from "@tanstack/react-table"
import { useMemo } from "preact/compat"
import { Skeleton } from "@/components/ui/skeleton.tsx"
import { TableControls } from "@/deduction_table/table_controls.tsx"
import { capitalCase } from "change-case"
import { PageControls, type AccountingDeductionTradeTotals } from "@/deduction_table/page_controls.tsx"
import { DeductionTableSelectionBar } from "@/deduction/DeductionTableSelectionBar.tsx"
import { FilterCard } from "@/deduction_table"
import { AccountingState } from "@/transactions/state.tsx"
import { Button } from "@/components/ui/button.tsx"
import { ActiveFilterIcon } from "@/global_filter"
import {
  openAccountingTransactionModal,
  AccountingTransactionModalId,
} from "@/accounting/accounting_transaction_modal_state"
import { coding_amount, display_accounting_amount } from "@/utils/transaction_amount"

const _COLORS = ["bg-orange-500", "bg-purple-500", "bg-pink-500", "bg-cyan-500"]

function is_trade_org_field_active(orgFieldConfigs?: OrgFieldConfig[]): boolean {
  return (
    orgFieldConfigs?.some(
      (config: OrgFieldConfig) =>
        config.field_name === "is_trade" && config.is_active && config.table_contexts.includes("accounting_table")
    ) ?? false
  )
}

function deduction_trade_totals(row: TransactionJson): AccountingDeductionTradeTotals {
  const amount = display_accounting_amount(row.kind, row.invoice_amount)
  if (row.kind !== "deduction" || amount >= 0) {
    return {
      trade_total: 0,
      non_trade_total: 0,
      unclassified_total: 0,
    }
  }

  if (!row.splits.length) {
    return {
      trade_total: 0,
      non_trade_total: 0,
      unclassified_total: amount,
    }
  }

  const totals: AccountingDeductionTradeTotals = {
    trade_total: 0,
    non_trade_total: 0,
    unclassified_total: 0,
  }
  for (const split of row.splits) {
    const split_amount = Number(split.amount)
    if (!Number.isFinite(split_amount)) {
      continue
    }
    // Preserve the split's real sign: a positive split reduces what's owed (books negative), while a
    // negative split is a credit/reversal that adds back (books positive). Forcing `-abs` here flipped
    // credit lines the wrong way, inflating non-trade and dumping a bogus positive into unclassified.
    const signed_amount = -split_amount
    if (split.is_trade === true) {
      totals.trade_total += signed_amount
    } else if (split.is_trade === false) {
      totals.non_trade_total += signed_amount
    } else {
      totals.unclassified_total += signed_amount
    }
  }

  const classified_total = totals.trade_total + totals.non_trade_total + totals.unclassified_total
  const residual = amount - classified_total
  if (Math.abs(residual) >= 0.005) {
    totals.unclassified_total += residual
  }
  return totals
}

interface AccountingTableProps {
  loading: boolean
  table: Table<TransactionJson>
  orgFieldConfigs?: OrgFieldConfig[]
  /** Passed through to TableControls: recompute export rows at export time (e.g. Guayaki RTM auto-split). */
  getExportRows?: (options: { autoSplit: boolean; transactionIds: string[] }) => Promise<TransactionJson[]>
}

export function TransactionTable({ loading, table, orgFieldConfigs, getExportRows }: AccountingTableProps) {
  let accountingState = AccountingState.use((as: any) => as!)
  let modalTransactionId = AccountingTransactionModalId.use((id: string | null) => id)
  const showTradeDeductionTotals = is_trade_org_field_active(orgFieldConfigs)

  const isRowSelected = accountingState.rowSelection && Object.keys(accountingState.rowSelection).length > 0

  function handleRowClick(row: Row<TransactionJson>, table: Table<TransactionJson>) {
    if (table.getIsSomeRowsSelected()) {
      row.toggleSelected()
      return
    }
    let ded = row.original
    openAccountingTransactionModal(ded.id)
  }

  let rows = table.getRowModel().rows

  return (
    <>
      <TableControls
        table={table as any as Table<DeductionListJson>}
        orgFieldConfigs={orgFieldConfigs}
        enableCsvColumnPicker={true}
        csvColumnPickerScope="accounting-transactions"
        showApprovalLogExport={true}
        getExportRows={getExportRows as any}
      />
      <div class="border w-full">
        <div class="overflow-auto w-full">
          <table class="w-full border-collapse" style={{ tableLayout: "auto" }}>
            <thead class="bg-slate-50">
              {table.getHeaderGroups().map((headerGroup, idx) => (
                <tr key={`${headerGroup.id}-${idx}`} className="border-b">
                  {headerGroup.headers.map((header, headerIdx) => (
                    <th
                      key={`${header.id}-${headerIdx}`}
                      className="px-1 py-1 text-left text-sm font-medium text-slate-700 whitespace-nowrap"
                      style={{
                        position: "sticky",
                        top: 0,
                        backgroundColor: "#f8fafc",
                        zIndex: 10,
                        boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                        maxWidth: "180px",
                        width: "auto",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={header.column.id ? capitalCase(header.column.id) : ""}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  {Array.from({ length: ACCOUNTING_COLUMNS.length }).map((_, cellIndex) => (
                    <td key={cellIndex} className="px-4 py-3 border-b">
                      <Skeleton className="w-[100px] h-5 rounded-full bg-slate-500" />
                    </td>
                  ))}
                </tr>
              ) : rows.length ? (
                rows.map((row, idx) => {
                  let isModalOpen = modalTransactionId === row.original.id
                  return (
                    <tr
                      key={`${row.id}-${idx}`}
                      className={`hover:bg-slate-50 ${isModalOpen ? "bg-blue-100" : ""}`}
                      onClick={() => handleRowClick(row, table)}
                      data-state={row.getIsSelected() && "selected"}>
                      {row.getVisibleCells().map((cell, cellIdx) => (
                        <td
                          className="px-1 py-1 border-b whitespace-nowrap text-xs"
                          style={{
                            maxWidth: "180px",
                            width: "auto",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={String(cell.getValue() || "")}
                          key={`${cell.id}-${cellIdx}`}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td colSpan={ACCOUNTING_COLUMNS.length} className="h-24 text-center">
                    No results.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {isRowSelected && (
        <DeductionTableSelectionBar
          table={table as any as Table<DeductionListJson>}
          isAccounting={true}
          orgFieldConfigs={orgFieldConfigs}
        />
      )}
      <div class="flex items-center justify-between p-2 px-4">
        <PageControls
          table={table}
          getRowAmount={(row) => display_accounting_amount(row.kind, row.invoice_amount)}
          getRowKind={(row) => row.kind}
          getRowEarlyPayDiscount={(row) => -coding_amount(row.kind, row.invoice_amount, row.discount_amount)}
          getRowDeductionTradeTotals={showTradeDeductionTotals ? deduction_trade_totals : undefined}
          showSplitTotals={true}
        />
      </div>
    </>
  )
}

function handleSetCodedFilter(filter: boolean | null) {
  AccountingState.set(state => {
    let current = state.columnFilters.find(f => f.id === "coded")?.value
    let columnFilters
    if (filter === current) {
      columnFilters = [...state.columnFilters]
      columnFilters = columnFilters.filter(f => f.id !== "coded")
      return { ...state, columnFilters }
    } else {
      columnFilters = [...state.columnFilters]
      columnFilters = columnFilters.filter(f => f.id !== "coded")
      columnFilters.push({ id: "coded", value: filter })
    }
    return { ...state, columnFilters }
  })
}

/** Toggle the "Backup since coding" view: lines coded before their backup arrived. */
function handleSetBackupSinceCodingFilter(active: boolean) {
  AccountingState.set(state => {
    let columnFilters = state.columnFilters.filter(f => f.id !== "backup_since_coding")
    if (!active) {
      columnFilters.push({ id: "backup_since_coding", value: true })
    }
    return { ...state, columnFilters }
  })
}

function handleSetKindFilter(filter: TransactionKind | "all") {
  AccountingState.set(state => {
    let current = state.columnFilters.find(f => f.id === "kind")?.value
    let columnFilters
    if (filter === current) {
      columnFilters = [...state.columnFilters]
      columnFilters = columnFilters.filter(f => f.id !== "kind")
    } else {
      columnFilters = [...state.columnFilters]
      columnFilters = columnFilters.filter(f => f.id !== "kind")
      columnFilters.push({ id: "kind", value: filter })
    }
    return { ...state, columnFilters }
  })
}

interface KindAndCodedFiltersProps {
  data?: TransactionJson[]
  kinds: TransactionKind[]
}

/** Toggle the quick "open deductions" view: everything not fully cleared (Pending + Validated (Uncoded)). */
function handleSetOpenFilter(currentValue: unknown) {
  set_cleared_column_filter(is_open_cleared_filter(currentValue) ? null : [...OPEN_CLEARED_STATES])
}

export function KindAndCodedFilters({ data, kinds }: KindAndCodedFiltersProps) {
  let { columnFilters } = AccountingState.use()
  let kindFilter = columnFilters.find(f => f.id === "kind")?.value as string | undefined
  let codedFilter = columnFilters.find(f => f.id === "coded")?.value as boolean | undefined
  let clearedFilter = columnFilters.find(f => f.id === "cleared")?.value
  let backupSinceCodingActive = columnFilters.find(f => f.id === "backup_since_coding")?.value === true
  let openFilterActive = is_open_cleared_filter(clearedFilter)

  let stats = useMemo(() => {
    let stats = {
      invoiceCount: 0,
      deductionCount: 0,
      prepaymentCount: 0,
      repaymentCount: 0,
      codedCount: 0,
      uncodedCount: 0,
      backupSinceCodingCount: 0,
    }
    if (!data) {
      return stats
    }
    for (let tx of data) {
      if (tx.kind === "invoice") {
        stats.invoiceCount++
      } else if (tx.kind === "deduction") {
        stats.deductionCount++
      } else if (tx.kind === "prepayment") {
        stats.prepaymentCount++
      } else if (tx.kind === "repayment") {
        stats.repaymentCount++
      }
    }
    for (let tx of data) {
      if (tx.coded) {
        stats.codedCount++
      } else {
        stats.uncodedCount++
      }
      if (tx.backup_since_coding) {
        stats.backupSinceCodingCount++
      }
    }
    return stats
  }, [data])

  return (
    <>
      <div className="flex gap-4">
        {kinds.includes("invoice") && (
          <FilterCard
            title="Invoice"
            count={stats.invoiceCount}
            active={kindFilter === "invoice"}
            color="text-blue-700"
            onClick={() => handleSetKindFilter("invoice")}
          />
        )}
        <FilterCard
          title="Deductions"
          count={stats.deductionCount}
          active={kindFilter === "deduction"}
          color="text-orange-700"
          onClick={() => handleSetKindFilter("deduction")}
        />
        <FilterCard
          title="Repayments"
          count={stats.repaymentCount}
          active={kindFilter === "repayment"}
          color="text-green-700"
          onClick={() => handleSetKindFilter("repayment")}
        />
      </div>
      <div className="flex gap-4 ">
        <Button
          variant={openFilterActive ? "secondary" : "outline"}
          onClick={() => handleSetOpenFilter(clearedFilter)}
          className="flex items-center gap-2">
          Show Open
          {openFilterActive && <ActiveFilterIcon />}
        </Button>
        <Button
          variant={codedFilter === true ? "secondary" : "outline"}
          onClick={() => handleSetCodedFilter(true)}
          className="flex items-center gap-2">
          Show Coded
          {codedFilter === true && <ActiveFilterIcon />}
        </Button>
        <Button
          variant={codedFilter === false ? "secondary" : "outline"}
          onClick={() => handleSetCodedFilter(false)}
          className="flex items-center gap-2">
          Show Uncoded
          {codedFilter === false && <ActiveFilterIcon />}
        </Button>
        {(stats.backupSinceCodingCount > 0 || backupSinceCodingActive) && (
          <Button
            variant={backupSinceCodingActive ? "secondary" : "outline"}
            onClick={() => handleSetBackupSinceCodingFilter(backupSinceCodingActive)}
            title="Lines coded while the deduction had no backup, whose backup has since arrived. Re-code them from the backup; saving the coding clears the flag."
            className="flex items-center gap-2">
            Backup since coding ({stats.backupSinceCodingCount})
            {backupSinceCodingActive && <ActiveFilterIcon />}
          </Button>
        )}
      </div>
    </>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("deduction_trade_totals", () => {
    it("allocates signed deduction amounts by split trade classification", () => {
      const row = {
        kind: "deduction",
        invoice_amount: -100,
        splits: [
          { amount: 70, is_trade: true },
          { amount: 20, is_trade: false },
          { amount: 10 },
        ],
      } as TransactionJson

      expect(deduction_trade_totals(row)).toEqual({
        trade_total: -70,
        non_trade_total: -20,
        unclassified_total: -10,
      })
    })

    it("keeps uncoded or partially coded residual amounts unclassified", () => {
      const row = {
        kind: "deduction",
        invoice_amount: -100,
        splits: [{ amount: 60, is_trade: true }],
      } as TransactionJson

      expect(deduction_trade_totals(row)).toEqual({
        trade_total: -60,
        non_trade_total: 0,
        unclassified_total: -40,
      })
    })

    it("nets a negative credit split back instead of forcing it negative", () => {
      // $100 deduction coded to non-trade, with a $20 credit line (negative split) on the same GL.
      // Net is -$80 non-trade; the credit must add back, not inflate non-trade + leak into unclassified.
      const row = {
        kind: "deduction",
        invoice_amount: -80,
        splits: [
          { amount: 100, is_trade: false },
          { amount: -20, is_trade: false },
        ],
      } as TransactionJson

      expect(deduction_trade_totals(row)).toEqual({
        trade_total: 0,
        non_trade_total: -80,
        unclassified_total: 0,
      })
    })
  })

  describe("is_trade_org_field_active", () => {
    it("only enables the footer split for active accounting-table is_trade fields", () => {
      expect(
        is_trade_org_field_active([
          {
            id: "cfg",
            org_id: "org",
            field_name: "is_trade",
            field_type: FieldType.Text,
            display_name: "Is Trade",
            display_order: 1,
            is_active: true,
            table_contexts: ["accounting_table"],
            created_at: "2026-01-01",
            updated_at: "2026-01-01",
          },
        ])
      ).toBe(true)
      expect(
        is_trade_org_field_active([
          {
            id: "cfg",
            org_id: "org",
            field_name: "is_trade",
            field_type: FieldType.Text,
            display_name: "Is Trade",
            display_order: 1,
            is_active: true,
            table_contexts: ["deduction_table"],
            created_at: "2026-01-01",
            updated_at: "2026-01-01",
          },
        ])
      ).toBe(false)
    })
  })
}
